import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, IconButton,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  Switch, FormControl, InputLabel, Select, MenuItem, Grid, Card,
  CardContent, Divider, Snackbar, Alert
} from '@mui/material';
import {
  Email, Campaign, Notifications, Add, Edit, Delete, Send, Close,
  Visibility, People, School, Person, CheckCircle
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const mockAnnouncements = [
  { id: 1, title: 'Parent-Teacher Conferences', body: 'Parent-teacher conferences are scheduled for January 25–26. Sign-up links will be sent via email.', audience: 'All', date: '2024-01-18', active: true },
  { id: 2, title: 'Science Fair Registration Open', body: 'Students interested in the Science Fair must register by February 1. See your science teacher for entry forms.', audience: 'Students', date: '2024-01-16', active: true },
  { id: 3, title: 'Chapel Service Change', body: 'This Friday\'s chapel service has been moved to 1:30 PM in the main auditorium.', audience: 'All', date: '2024-01-15', active: true },
  { id: 4, title: 'Holiday Break Schedule', body: 'A reminder that Winter Break runs February 15–19. School will resume February 22.', audience: 'All', date: '2024-01-10', active: false },
  { id: 5, title: 'Lunch Menu Update', body: 'The cafeteria will be serving a special Thanksgiving menu on November 20.', audience: 'All', date: '2023-11-15', active: false },
];

const mockTemplates = [
  { id: 1, name: 'Application Received', subject: 'We received your application — Eagles Refuge Academy', category: 'Applications', lastUsed: '2024-01-18' },
  { id: 2, name: 'Application Approved', subject: 'Congratulations! Your application has been approved', category: 'Applications', lastUsed: '2024-01-17' },
  { id: 3, name: 'Application Rejected', subject: 'Regarding your application to Eagles Refuge Academy', category: 'Applications', lastUsed: '2024-01-10' },
  { id: 4, name: 'Waitlist Notification', subject: 'Your application has been placed on the waitlist', category: 'Applications', lastUsed: '2024-01-08' },
  { id: 5, name: 'Welcome New Student', subject: 'Welcome to Eagles Refuge Academy!', category: 'Enrollment', lastUsed: '2024-01-06' },
  { id: 6, name: 'Attendance Alert', subject: 'Attendance concern for your child', category: 'Attendance', lastUsed: '2024-01-15' },
  { id: 7, name: 'Grade Report', subject: 'Quarterly grade report — Eagles Refuge Academy', category: 'Academics', lastUsed: '2023-12-20' },
  { id: 8, name: 'Payment Reminder', subject: 'Tuition payment reminder — Eagles Refuge Academy', category: 'Finance', lastUsed: '2024-01-05' },
];

const notificationSettings = [
  { id: 'new_application', label: 'New Application Submitted', description: 'Notify admins when a new application arrives', defaultOn: true },
  { id: 'attendance_low', label: 'Low Attendance Alert', description: 'Alert parents when attendance drops below 90%', defaultOn: true },
  { id: 'grade_report', label: 'Quarterly Grade Reports', description: 'Send grade reports to parents at end of each quarter', defaultOn: true },
  { id: 'payment_due', label: 'Tuition Payment Reminders', description: 'Send payment reminders 7 days before due date', defaultOn: true },
  { id: 'event_reminder', label: 'Event Reminders', description: 'Send reminders 24 hours before scheduled events', defaultOn: false },
  { id: 'announcement', label: 'School Announcements', description: 'Push announcements to parent/student portals', defaultOn: true },
  { id: 'emergency', label: 'Emergency Notifications', description: 'Immediate alerts for emergency situations', defaultOn: true },
];

const CommunicationAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/announcements')) return 0;
    if (location.pathname.includes('/templates')) return 1;
    if (location.pathname.includes('/notifications')) return 2;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [announcements, setAnnouncements] = useState(mockAnnouncements);
  const [announcementDialog, setAnnouncementDialog] = useState({ open: false, item: null });
  const [templateDialog, setTemplateDialog] = useState({ open: false, item: null });
  const [notifSettings, setNotifSettings] = useState(Object.fromEntries(notificationSettings.map(n => [n.id, n.defaultOn])));
  const [composeDialog, setComposeDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({ title: '', body: '', audience: 'All' });

  const handleSaveAnnouncement = () => {
    if (announcementDialog.item) {
      setAnnouncements(prev => prev.map(a => a.id === announcementDialog.item.id ? { ...a, ...form } : a));
      setSnackbar({ open: true, message: 'Announcement updated', severity: 'success' });
    } else {
      setAnnouncements(prev => [...prev, { id: Date.now(), ...form, date: new Date().toISOString().split('T')[0], active: true }]);
      setSnackbar({ open: true, message: 'Announcement created and published', severity: 'success' });
    }
    setAnnouncementDialog({ open: false, item: null });
    setForm({ title: '', body: '', audience: 'All' });
  };

  const handleToggleAnnouncement = (id) => {
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, active: !a.active } : a));
  };

  const handleDeleteAnnouncement = (id) => {
    setAnnouncements(prev => prev.filter(a => a.id !== id));
    setSnackbar({ open: true, message: 'Announcement deleted', severity: 'info' });
  };

  const openEdit = (item) => {
    setForm({ title: item.title, body: item.body, audience: item.audience });
    setAnnouncementDialog({ open: true, item });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Communication</Typography>
          <Typography variant="body2" color="text.secondary">Manage announcements, email templates, and notification settings</Typography>
        </Box>
        <Button variant="contained" startIcon={<Send />} onClick={() => setComposeDialog(true)}>
          Compose Email
        </Button>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab icon={<Campaign />} label="Announcements" iconPosition="start" />
            <Tab icon={<Email />} label="Email Templates" iconPosition="start" />
            <Tab icon={<Notifications />} label="Notification Settings" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Announcements Tab */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>School Announcements</Typography>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => { setForm({ title: '', body: '', audience: 'All' }); setAnnouncementDialog({ open: true, item: null }); }}>
                New Announcement
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {announcements.map(ann => (
                <Paper key={ann.id} variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: ann.active ? 'success.main' : 'grey.400' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{ann.title}</Typography>
                        <Chip label={ann.audience} size="small" variant="outlined" />
                        <Chip label={ann.active ? 'Active' : 'Inactive'} size="small" color={ann.active ? 'success' : 'default'} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{ann.body}</Typography>
                      <Typography variant="caption" color="text.secondary">Posted {ann.date}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => openEdit(ann)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleToggleAnnouncement(ann.id)} color={ann.active ? 'warning' : 'success'}>
                        {ann.active ? <Notifications fontSize="small" /> : <CheckCircle fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteAnnouncement(ann.id)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Email Templates Tab */}
        {tab === 1 && (
          <TableContainer>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" size="small" startIcon={<Add />}>New Template</Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Template Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject Line</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockTemplates.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{t.name}</TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{t.subject}</Typography></TableCell>
                    <TableCell><Chip label={t.category} size="small" variant="outlined" /></TableCell>
                    <TableCell>{t.lastUsed}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small"><Visibility fontSize="small" /></IconButton>
                        <IconButton size="small"><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => { setTemplateDialog({ open: true, item: t }); }}><Send fontSize="small" /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Notification Settings Tab */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Notification Settings</Typography>
            <List>
              {notificationSettings.map((setting, i) => (
                <React.Fragment key={setting.id}>
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Notifications color={notifSettings[setting.id] ? 'primary' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>{setting.label}</Typography>}
                      secondary={setting.description}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={!!notifSettings[setting.id]}
                        onChange={(e) => setNotifSettings(prev => ({ ...prev, [setting.id]: e.target.checked }))}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  {i < notificationSettings.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={() => setSnackbar({ open: true, message: 'Notification settings saved', severity: 'success' })}>
                Save Settings
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Announcement Dialog */}
      <Dialog open={announcementDialog.open} onClose={() => setAnnouncementDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {announcementDialog.item ? 'Edit Announcement' : 'New Announcement'}
          <IconButton size="small" onClick={() => setAnnouncementDialog({ open: false, item: null })}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Title" fullWidth value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required />
            <TextField label="Body" multiline rows={4} fullWidth value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} required />
            <FormControl fullWidth>
              <InputLabel>Audience</InputLabel>
              <Select value={form.audience} label="Audience" onChange={e => setForm(p => ({ ...p, audience: e.target.value }))}>
                <MenuItem value="All">All (Students, Parents, Staff)</MenuItem>
                <MenuItem value="Students">Students Only</MenuItem>
                <MenuItem value="Parents">Parents Only</MenuItem>
                <MenuItem value="Staff">Staff Only</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAnnouncementDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAnnouncement} disabled={!form.title || !form.body}>
            {announcementDialog.item ? 'Save Changes' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compose Email Dialog */}
      <Dialog open={composeDialog} onClose={() => setComposeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          Compose Email
          <IconButton size="small" onClick={() => setComposeDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>To</InputLabel>
              <Select label="To">
                <MenuItem value="all">All Parents & Students</MenuItem>
                <MenuItem value="parents">All Parents</MenuItem>
                <MenuItem value="students">All Students</MenuItem>
                <MenuItem value="staff">All Staff</MenuItem>
                <MenuItem value="grade5">5th Grade Families</MenuItem>
                <MenuItem value="individual">Individual (enter below)</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Subject" fullWidth />
            <FormControl fullWidth>
              <InputLabel>Template (optional)</InputLabel>
              <Select label="Template (optional)">
                <MenuItem value="">None — blank email</MenuItem>
                {mockTemplates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Message" multiline rows={6} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setComposeDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<Send />} onClick={() => { setComposeDialog(false); setSnackbar({ open: true, message: 'Email sent successfully', severity: 'success' }); }}>Send Email</Button>
        </DialogActions>
      </Dialog>

      {/* Send Template Dialog */}
      <Dialog open={templateDialog.open} onClose={() => setTemplateDialog({ open: false, item: null })} maxWidth="xs">
        <DialogTitle>Send Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Send <strong>{templateDialog.item?.name}</strong> to:</Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Recipients</InputLabel>
            <Select label="Recipients">
              <MenuItem value="all">All Parents</MenuItem>
              <MenuItem value="individual">Specific Parent</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" startIcon={<Send />} onClick={() => { setTemplateDialog({ open: false, item: null }); setSnackbar({ open: true, message: 'Template email sent', severity: 'success' }); }}>Send</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationAdmin;
